import { useCallback, useEffect, useRef, useState } from 'react';

import { readLastActivityAt, writeLastActivityAt } from '../../../lib/helpers/idle-activity.helpers';
import { getOnlineValue } from '../../../lib/stores/connectivity.store';
import { ACTIVITY_EVENTS, ACTIVITY_WRITE_THROTTLE_MS, IDLE_TICK_MS, IDLE_WARNING_MS } from './idle-sign-out.constants';

interface IUseIdleTimerArgs {
  userId: string;
  timeoutMs: number;
}

interface IIdleTimer {
  // Time left while the warning is due, otherwise null.
  warningRemainingMs: number | null;
  expired: boolean;
  keepAlive: () => void;
}

// Tracks inactivity against the last-activity stamp shared by every tab. Once expired, activity
// revives the session only while offline, where the sign-out is deferred until the connection returns.
export const useIdleTimer = ({ userId, timeoutMs }: IUseIdleTimerArgs): IIdleTimer => {
  const [warningRemainingMs, setWarningRemainingMs] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  const keepAliveRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const seeded = readLastActivityAt(userId);
    let lastActivity = seeded ?? Date.now();
    if (seeded === null) writeLastActivityAt(userId, lastActivity);
    let lastWrite = lastActivity;
    let warning = false;

    const evaluate = () => {
      const stored = readLastActivityAt(userId);
      if (stored !== null && stored > lastActivity) lastActivity = stored;
      const remaining = timeoutMs - (Date.now() - lastActivity);
      warning = remaining > 0 && remaining <= IDLE_WARNING_MS;
      setExpired(remaining <= 0);
      setWarningRemainingMs(warning ? remaining : null);
    };

    const recordActivity = (force: boolean) => {
      const now = Date.now();
      if (now - lastActivity >= timeoutMs && getOnlineValue()) return;
      lastActivity = now;
      if (force || now - lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
        writeLastActivityAt(userId, now);
        lastWrite = now;
      }
      if (force || warning) evaluate();
    };

    const onActivity = () => recordActivity(false);
    const onVisible = () => {
      if (!document.hidden) evaluate();
    };
    keepAliveRef.current = () => recordActivity(true);

    // Explicit removal rather than an AbortSignal, which Safari before 15 ignores.
    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(evaluate, IDLE_TICK_MS);
    evaluate();

    return () => {
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
      keepAliveRef.current = () => undefined;
    };
  }, [userId, timeoutMs]);

  const keepAlive = useCallback(() => keepAliveRef.current(), []);

  return { warningRemainingMs, expired, keepAlive };
};
